import { Injectable, inject, NgZone, runInInjectionContext, Injector } from '@angular/core';
import {
  Auth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  User,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  ActionCodeSettings,
} from '@angular/fire/auth';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  collectionGroup,
} from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, filter, take, map, combineLatest } from 'rxjs';
import { ToastService } from './toast.service';
import {
  FirestoreTimestampField,
  firestoreTimestampNow,
  prepareFirestoreWrite,
} from '../utils/firestore-timestamp.util';

export interface UserProfile {
  id: string;
  authenticationId?: string;
  email: string;
  firstName: string;
  lastName: string;
  createdOn: FirestoreTimestampField;
  phone?: string;
}

export interface SupportEmployee {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  businessId: string;
  businessType: string;
  userId?: string;
  role: string;
  createdOn: FirestoreTimestampField;
  version: number;
}

const BUSINESS_TYPE = 'support';

/** Post-login access for Support web (business type = support), aligned with Admin employee checks. */
export type SupportLoginEligibility = 'ok' | 'invalid_user' | 'not_authorized';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private injector = inject(Injector);
  private toastService = inject(ToastService);

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean | null>(null);
  private userProfileSubject = new BehaviorSubject<UserProfile | null>(null);
  private isRegisteredSubject = new BehaviorSubject<boolean | null>(null);
  private hasBusinessInfoSubject = new BehaviorSubject<boolean | null>(null);

  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public userProfile$ = this.userProfileSubject.asObservable();
  public isRegistered$ = this.isRegisteredSubject.asObservable();
  public hasBusinessInfo$ = this.hasBusinessInfoSubject.asObservable();

  constructor() {
    onAuthStateChanged(this.auth, (user) => {
      this.ngZone.run(() => {
        runInInjectionContext(this.injector, () => {
          this.currentUserSubject.next(user);
          this.isAuthenticatedSubject.next(!!user);
          if (user) {
            this.isRegisteredSubject.next(null);
            this.hasBusinessInfoSubject.next(null);
            this.checkUserProfileEnabled(user.uid);
          } else {
            this.userProfileSubject.next(null);
            this.isRegisteredSubject.next(false);
            this.hasBusinessInfoSubject.next(false);
          }
        });
      });
    });

  }

  waitForAuthInitialization(): Promise<void> {
    return firstValueFrom(
      this.isAuthenticated$.pipe(
        filter((v) => v !== null),
        take(1),
        map(() => void 0)
      )
    );
  }

  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  getUserProfile(): UserProfile | null {
    return this.userProfileSubject.value;
  }

  cacheUserProfile(profile: UserProfile): void {
    this.userProfileSubject.next(profile);
  }

  refreshAfterRegistration(profile: UserProfile): void {
    this.userProfileSubject.next(profile);
    this.isRegisteredSubject.next(true);
    this.hasBusinessInfoSubject.next(false);
  }

  async signInWithGoogle(): Promise<{ success: boolean; error?: string | null; canceled?: boolean }> {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(this.auth, provider);
      console.log('Google sign-in success:', result.user?.email);
      return { success: true };
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        return { success: false, error: null, canceled: true };
      }
      console.error('Google sign-in error:', error.code, error.message);
      return { success: false, error: error.message || 'Sign in failed' };
    }
  }

  async sendSignInLink(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      let continueUrl = `${this.getAuthDomain()}/auth/action`;
      const shortId =
        localStorage.getItem('shortId')?.trim() ||
        localStorage.getItem('pendingEmailSignInInviteId')?.trim();
      if (shortId) {
        localStorage.setItem('pendingEmailSignInInviteId', shortId);
        localStorage.setItem('shortId', shortId);
        continueUrl = `${continueUrl}?invite=${encodeURIComponent(shortId)}`;
      }
      const actionCodeSettings: ActionCodeSettings = {
        url: continueUrl,
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(this.auth, email, actionCodeSettings);
      localStorage.setItem('emailForSignIn', email);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to send sign-in link' };
    }
  }

  async signInWithEmailLinkHandler(url: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!isSignInWithEmailLink(this.auth, url)) {
        return { success: false, error: 'Not a valid sign-in link' };
      }
      let email = localStorage.getItem('emailForSignIn');
      if (!email) {
        email = window.prompt('Please provide your email for confirmation') || '';
      }
      await signInWithEmailLink(this.auth, email, url);
      localStorage.removeItem('emailForSignIn');
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Sign-in failed' };
    }
  }

  async signOut(): Promise<void> {
    await signOut(this.auth);
    localStorage.removeItem('emailForSignIn');
  }

  /**
   * Support login rules (same pattern as Admin: user doc + employee of the right business type).
   * Invitation flow (fromInvitationLink + businessId) skips checks until the invite is processed.
   */
  async evaluateSupportLoginEligibility(authenticationId: string): Promise<SupportLoginEligibility> {
    if (localStorage.getItem('fromInvitationLink') === 'true' && localStorage.getItem('businessId')) {
      return 'ok';
    }
    const userExists = await this.checkUserExists(authenticationId);
    if (!userExists) {
      return 'invalid_user';
    }
    const hasSupport = await this.checkSupportBusinessInfo(authenticationId);
    return hasSupport ? 'ok' : 'not_authorized';
  }

  /**
   * If the user may not use Support, show the appropriate error and sign out.
   * @returns true when access is allowed
   */
  async assertSupportAccountAccess(authenticationId: string): Promise<boolean> {
    const gate = await this.evaluateSupportLoginEligibility(authenticationId);
    if (gate === 'ok') {
      return true;
    }
    const message =
      gate === 'invalid_user' ? 'Invalid user.' : 'User not authorized.';
    this.toastService.showError(message);
    await this.signOut();
    return false;
  }

  /**
   * After Firebase sign-in (Google or email link), wait for profile/employee resolution then
   * enforce Support access and navigate to tickets, registration, or profile.
   */
  async completeSupportSignInNavigation(): Promise<void> {
    const user = this.getCurrentUser();
    if (!user) {
      await this.router.navigate(['/index']);
      return;
    }
    await firstValueFrom(
      combineLatest([
        this.isAuthenticated$,
        this.hasBusinessInfo$,
        this.isRegistered$,
      ]).pipe(
        filter(([a, b, r]) => a !== null && b !== null && r !== null),
        take(1)
      )
    );
    const allowed = await this.assertSupportAccountAccess(user.uid);
    if (!allowed) {
      await this.router.navigate(['/index']);
      return;
    }
    const hasBiz = this.hasBusinessInfoSubject.value === true;
    const isReg = this.isRegisteredSubject.value === true;
    if (hasBiz) {
      await this.router.navigate(['/app/tickets']);
    } else if (!isReg) {
      await this.router.navigate(['/user-registration']);
    } else {
      await this.router.navigate(['/app/profile']);
    }
  }

  async checkUserExists(authenticationId: string): Promise<boolean> {
    try {
      const q = query(collection(this.firestore, 'users'), where('authenticationId', '==', authenticationId));
      const snap = await this.readDocs(q);
      if (!snap.empty) return true;
      const q2 = query(collection(this.firestore, 'users'), where('id', '==', authenticationId));
      const snap2 = await this.readDocs(q2);
      return !snap2.empty;
    } catch {
      return false;
    }
  }

  async getUserDocumentId(authenticationId: string): Promise<string | null> {
    try {
      const q = query(collection(this.firestore, 'users'), where('authenticationId', '==', authenticationId));
      const snap = await this.readDocs(q);
      if (!snap.empty) return snap.docs[0].id;
      const q2 = query(collection(this.firestore, 'users'), where('id', '==', authenticationId));
      const snap2 = await this.readDocs(q2);
      return snap2.empty ? null : snap2.docs[0].id;
    } catch {
      return null;
    }
  }

  async getCachedUserDocumentIdOrFetch(authenticationId: string): Promise<string | null> {
    const cached = this.userProfileSubject.value;
    if (cached?.id) return cached.id;
    return this.getUserDocumentId(authenticationId);
  }

  async getCurrentEmployee(authenticationId: string): Promise<{ id: string; role: string; businessId: string } | null> {
    try {
      const userDocId = await this.getUserDocumentId(authenticationId);
      if (!userDocId) return null;
      const q = query(
        collectionGroup(this.firestore, 'employees'),
        where('userId', '==', userDocId),
        where('businessType', '==', BUSINESS_TYPE)
      );
      const snap = await this.readDocs(q);
      if (snap.empty) return null;

      const empDoc = snap.docs[0];
      const data = empDoc.data();
      const businessId = data['businessId'] ?? '';
      if (!businessId) return null;
      return { id: empDoc.id, role: data['role'] ?? 'agent', businessId };
    } catch (error) {
      console.error('Error getting current employee:', error);
      return null;
    }
  }

  async getBusinessId(authenticationId: string): Promise<string | null> {
    const emp = await this.getCurrentEmployee(authenticationId);
    return emp?.businessId ?? null;
  }

  /** Called on invitation link acceptance. Creates/updates employee record. */
  async createInvitedEmployee(authenticationId: string, businessId: string, userName: string): Promise<void> {
    const invitedEmail = localStorage.getItem('invitedEmail');
    if (invitedEmail) {
      const currentEmail = this.auth.currentUser?.email?.trim().toLowerCase();
      if (!currentEmail || currentEmail !== invitedEmail.toLowerCase()) {
        this.clearInvitationStorage();
        throw new Error('This invitation was sent to a different email address.');
      }
    }

    const userDocId = await this.ensureUserDocumentId(authenticationId);
    const invitedEmployeeId = localStorage.getItem('invitedEmployeeId');

    if (invitedEmployeeId) {
      const empRef = doc(this.firestore, 'businesses', businessId, 'employees', invitedEmployeeId);
      const empSnap = await this.readDoc(empRef);
      if (empSnap.exists()) {
        await this.writeUpdateDoc(empRef, { userId: userDocId });
        this.clearInvitationStorage();
        return;
      }
    }

    const existingQ = query(
      collection(this.firestore, 'businesses', businessId, 'employees'),
      where('userId', '==', userDocId)
    );
    const existingSnap = await this.readDocs(existingQ);
    if (!existingSnap.empty) {
      this.clearInvitationStorage();
      return;
    }

    const userEmail = this.auth.currentUser?.email || '';
    const employeeData: SupportEmployee = {
      id: userDocId,
      name: userName,
      email: userEmail,
      businessId,
      businessType: BUSINESS_TYPE,
      userId: userDocId,
      role: 'agent',
      createdOn: firestoreTimestampNow(),
      version: 1,
    };
    await this.writeSetDoc(doc(this.firestore, 'businesses', businessId, 'employees', userDocId), employeeData);
    this.clearInvitationStorage();
  }

  private clearInvitationStorage(): void {
    localStorage.removeItem('fromInvitationLink');
    localStorage.removeItem('businessId');
    localStorage.removeItem('invitedEmail');
    localStorage.removeItem('invitedEmployeeId');
  }

  private async ensureUserDocumentId(authenticationId: string): Promise<string> {
    const existing = await this.getUserDocumentId(authenticationId);
    if (existing) return existing;

    const currentUser = this.auth.currentUser;
    const email = currentUser?.email || '';
    const displayName = (currentUser?.displayName || '').trim();
    const nameParts = displayName ? displayName.split(/\s+/).filter(Boolean) : [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const newUserRef = doc(collection(this.firestore, 'users'));
    const userDocId = newUserRef.id;
    await this.writeSetDoc(newUserRef, {
      id: userDocId,
      authenticationId,
      email,
      firstName,
      lastName,
      createdOn: firestoreTimestampNow(),
      version: 1,
    });
    return userDocId;
  }

  private async checkUserProfileEnabled(uid: string): Promise<void> {
    try {
      const userDocId = await this.getUserDocumentId(uid);

      if (userDocId) {
        const q = query(collection(this.firestore, 'users'), where('authenticationId', '==', uid));
        const snap = await this.readDocs(q);
        const userDoc = snap.docs[0]
          ?? (await this.readDocs(query(collection(this.firestore, 'users'), where('id', '==', uid)))).docs[0];

        if (userDoc) {
          this.userProfileSubject.next({ ...userDoc.data(), id: userDoc.id } as UserProfile);
          this.isRegisteredSubject.next(true);
        } else {
          this.userProfileSubject.next(null);
          this.isRegisteredSubject.next(false);
        }

        const fromInvitationLink = localStorage.getItem('fromInvitationLink') === 'true';
        const storedBusinessId = localStorage.getItem('businessId');

        if (fromInvitationLink && storedBusinessId) {
          try {
            const displayName = this.auth.currentUser?.displayName || this.auth.currentUser?.email || 'Support User';
            await this.createInvitedEmployee(uid, storedBusinessId, displayName);
            this.hasBusinessInfoSubject.next(true);
          } catch (error: any) {
            this.toastService.showError(error?.message || 'Failed to process invitation');
            this.clearInvitationStorage();
            const hasBiz = await this.checkSupportBusinessInfo(uid);
            this.hasBusinessInfoSubject.next(hasBiz);
          }
        } else {
          const hasBiz = await this.checkSupportBusinessInfo(uid);
          this.hasBusinessInfoSubject.next(hasBiz);
        }
      } else {
        const fromInvitationLink = localStorage.getItem('fromInvitationLink') === 'true';
        const storedBusinessId = localStorage.getItem('businessId');

        if (fromInvitationLink && storedBusinessId) {
          this.isRegisteredSubject.next(false);
          this.hasBusinessInfoSubject.next(true);
          const currentUser = this.auth.currentUser;
          if (currentUser) {
            const name = currentUser.displayName || currentUser.email || 'Support User';
            try {
              await this.createInvitedEmployee(uid, storedBusinessId, name);
            } catch (error: any) {
              this.toastService.showError(error?.message || 'Failed to process invitation');
            }
          }
        } else {
          this.isRegisteredSubject.next(false);
          this.hasBusinessInfoSubject.next(false);
        }
      }
    } catch (error) {
      console.error('Error checking user profile:', error);
      this.isRegisteredSubject.next(false);
      this.hasBusinessInfoSubject.next(false);
    }
  }

  async checkSupportBusinessInfo(authenticationId: string): Promise<boolean> {
    try {
      const userDocId = await this.getUserDocumentId(authenticationId);
      if (!userDocId) return false;

      const q = query(
        collectionGroup(this.firestore, 'employees'),
        where('userId', '==', userDocId),
        where('businessType', '==', BUSINESS_TYPE)
      );
      const snap = await this.readDocs(q);
      if (!snap.empty) return true;

      const userEmail = this.auth.currentUser?.email?.trim().toLowerCase();
      if (userEmail) {
        const emailQ = query(
          collectionGroup(this.firestore, 'employees'),
          where('email', '==', userEmail),
          where('businessType', '==', BUSINESS_TYPE)
        );
        try {
          const emailSnap = await this.readDocs(emailQ);
          if (!emailSnap.empty) return true;
        } catch (error: any) {
          if (error?.code !== 'failed-precondition') {
            throw error;
          }
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking support business info:', error);
      return false;
    }
  }

  private readDocs(q: any): Promise<any> {
    return this.ngZone.run(() =>
      runInInjectionContext(this.injector, () => getDocs(q))
    );
  }

  private readDoc(ref: any): Promise<any> {
    return this.ngZone.run(() =>
      runInInjectionContext(this.injector, () => getDoc(ref))
    );
  }

  private writeSetDoc(ref: any, data: any): Promise<void> {
    return this.ngZone.run(() =>
      runInInjectionContext(this.injector, () =>
        setDoc(ref, prepareFirestoreWrite(data) as Record<string, unknown>)
      )
    );
  }

  private writeUpdateDoc(ref: any, data: any): Promise<void> {
    return this.ngZone.run(() =>
      runInInjectionContext(this.injector, () =>
        updateDoc(ref, prepareFirestoreWrite(data) as never)
      )
    );
  }

  private getAuthDomain(): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      return `${protocol}//${hostname}${port ? ':' + port : ':4201'}`;
    }
    return `${protocol}//${hostname}`;
  }
}
